import * as React from 'react';
import {
    DeviceType,
    PageType,
    AdObj,
    Size,
    Vertical,
    Subvertical,
} from '../../../shared/types/ast';
import { useAd } from '../../hooks/ad';
import { Wallpaper } from '../wallpaper';
import { Placeholder } from '../placeholder';
import { Banner } from '../banner';
import { ErrorBoundary } from '../error-boundary';
import { displayAdsLog } from '../../utils/debug';
import { getThresholdProximityFromExperiment } from '../../config';
import { UNLEASH_FEATURE_NAMES } from '../../../shared/unleash/feature-names';

export type Props = {
    deviceType: DeviceType;
    vertical?: Vertical;
    subvertical?: Subvertical;
    pageType?: PageType;
};

function Horseshoe(props: Props) {
    displayAdsLog('Inside horseshoe component');
    const { deviceType } = props;

    const { adObj: topboardAdObj, sizes: topboardSizes } = useAd(
        'topboard',
        false,
    );

    const { adObj: skyscraperleftAdObj, sizes: skyscraperleftSizes } = useAd(
        'skyscraperleft_1',
        false,
    );

    const { adObj: skyscraperrightAdObj, sizes: skyscraperrightSizes } = useAd(
        'skyscraperright_1',
        false,
    );

    const { adObj: wallpaperAdObj } = useAd('wallpaper', false);

    const { adObj: testAdObj } = useAd('test_1', false);

    const isChatBotAd =
        skyscraperrightAdObj &&
        skyscraperrightAdObj.targetId === 'skyscraperright_1' &&
        skyscraperrightAdObj.source === 'rtb' &&
        !!skyscraperrightAdObj.height &&
        !!skyscraperrightAdObj.width &&
        skyscraperrightAdObj.height === 4 &&
        skyscraperrightAdObj.width === 4;

    const exp = getThresholdProximityFromExperiment(
        UNLEASH_FEATURE_NAMES.enableTopBannerThreshProx,
    );
    const thresholdAndProximity = exp
        ? {
              threshold: exp.threshold,
              proximity: exp.proximity,
          }
        : {};

    switch (deviceType) {
        case 'desktop':
            return (
                <div
                    className={`banners adsbox${
                        skyscraperleftAdObj ? ' banners__is-horse-shoe' : ''
                    } ${
                        isChatBotAd
                            ? 'banners__right-skyscraper-is-chat-bot'
                            : ''
                    }`}
                    data-banner-pos="topbanner"
                    style={{ height: '165px' }}
                    aria-hidden="true"
                >
                    <div>
                        <div className="banners__left-skyscraper">
                            <div className="banners__sticky-skyscraper">
                                <HorseshoeSwitch
                                    className=""
                                    id="skyscraperleft_1"
                                    adObj={skyscraperleftAdObj}
                                    sizes={skyscraperleftSizes}
                                />
                            </div>
                        </div>

                        <HorseshoeSwitch
                            {...thresholdAndProximity}
                            className="banners__topboard"
                            id="topboard"
                            adObj={topboardAdObj}
                            sizes={topboardSizes}
                        />

                        <div className="banners__right-skyscraper">
                            <div
                                id="skyscraperright_1-sticky-container"
                                className="banners__sticky-skyscraper"
                            >
                                <HorseshoeSwitch
                                    className=""
                                    id="skyscraperright_1"
                                    adObj={skyscraperrightAdObj}
                                    sizes={skyscraperrightSizes}
                                />
                            </div>
                        </div>

                        {wallpaperAdObj ? (
                            <Wallpaper
                                className="hidden"
                                id="wallpaper"
                                targetId={wallpaperAdObj.targetId}
                            />
                        ) : (
                            <Placeholder className="hidden" id="wallpaper" />
                        )}
                        <HorseshoeSwitch
                            className="hidden"
                            id="test_1"
                            adObj={testAdObj}
                        />
                    </div>
                </div>
            );
        case 'tablet':
            return (
                <div
                    className={'banners adsbox banners__is-horse-shoe'}
                    data-banner-pos="topbanner"
                    style={{ height: '165px' }}
                    aria-hidden="true"
                >
                    <div>
                        <HorseshoeSwitch
                            {...thresholdAndProximity}
                            className="banners__topboard"
                            id="topboard"
                            adObj={topboardAdObj}
                            sizes={topboardSizes}
                        />

                        <HorseshoeSwitch
                            className="hidden"
                            id="test_1"
                            adObj={testAdObj}
                        />
                    </div>
                </div>
            );
        case 'mobile':
            return (
                <HorseshoeSwitch
                    className="hidden"
                    id="test_1"
                    adObj={testAdObj}
                />
            );
    }
}

const HorseshoeWithErrorBoundary = (props: Props) => (
    <ErrorBoundary fallback={null}>
        <Horseshoe {...props} />
    </ErrorBoundary>
);

export default HorseshoeWithErrorBoundary;

type HorseshoeSwitchProps = {
    id: string;
    className?: string;
    proximity?: number;
    threshold?: number;
    adObj?: AdObj;
    sizes?: Size[];
};

function HorseshoeSwitch(props: HorseshoeSwitchProps) {
    const { id, className, proximity, adObj, threshold } = props;

    if (adObj) {
        return (
            <Banner
                id={id}
                targetId={adObj.targetId}
                className={className}
                proximity={proximity}
                threshold={threshold}
            />
        );
    }
    return <Placeholder id={id} className={className} />;
}
